// M46 — Stale-bundle context + hook.
//
// Lives in its own module (not co-located with the provider) so the
// provider file can be edited without breaking React Fast Refresh —
// react-refresh/only-export-components flags a file that exports both
// a component and a non-component. Same split pattern as
// useInvite.ts / InviteProvider.tsx.

import { createContext, useContext } from 'react';

export interface StaleBundleValue {
  /** The newer version available, or null when the running bundle is
   *  in sync with the latest published changelog (or when we don't
   *  know yet / silently failed to find out). */
  latestVersion: string | null;
  /** Convenience boolean for "the loaded bundle is behind." */
  isStale: boolean;
  /** The version the SERVER is serving right now, when it is newer than the
   *  one this tab is running; null otherwise.
   *
   *  Deliberately separate from `latestVersion`, which answers a different
   *  question. `latestVersion` is "there are release notes you have not read"
   *  and drives the What's-new dot. This one is "the code under you has moved",
   *  which is true the instant a deploy lands — before any changelog row is
   *  written, and true even for a release that never gets one. It is the signal
   *  that predicts a chunk 404, so it is the one the update bar reads. */
  deployedVersion: string | null;
}

const DEFAULT: StaleBundleValue = { latestVersion: null, isStale: false, deployedVersion: null };

export const StaleBundleContext = createContext<StaleBundleValue>(DEFAULT);

/** Read the stale-bundle state. Returns all-null when no provider is
 *  mounted, so the badge in the shell, the banner on /changelog and the
 *  update bar all behave correctly in tests that don't wrap them. */
export function useStaleBundle(): StaleBundleValue {
  return useContext(StaleBundleContext);
}
